<script>
import { mapGetters } from 'vuex'
import dayjs from '@/services/date'
import baseMixin from '@/mixins/baseMixin'
import BookingDetailHeader from '@/components/booking/bookingList/side-panel/booking-detail-header.vue'
import BookingDetailsTab from '@/components/booking/bookingList/side-panel/booking-details-tab.vue'
import BookingDaysTable from '@/components/booking/bookingList/side-panel/booking-days-table.vue'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingType from '@/shared/constants/booking/BookingType'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import CustomTooltip from '../custom-tooltip.vue'

export default {
  name: 'BookingSidePanel',
  components: {
    BookingDetailHeader,
    BookingDetailsTab,
    BookingDaysTable,
    CustomTooltip,
  },
  mixins: [baseMixin, featureFlagMixin],
  props: {
    value: { type: Boolean, default: false },
    booking: { type: Object, default: null },
  },
  data() {
    return {
      tab: 0,
      BookingStatusLabelType,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
    }),
    visible: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    drawerWidth() {
      return this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.smAndDown
        ? '100%'
        : 720
    },

    _groupMode() {
      return !!(
        this.booking &&
        this.booking.groupId &&
        this.booking.groupId !== 0
      )
    },

    // normalized blocks (either blockDetails or single outer booking)
    _blocks() {
      if (!this.booking) return []
      if (this._groupMode) {
        const arr = Array.isArray(this.booking.blockDetails)
          ? this.booking.blockDetails
          : []
        return arr
          .filter(
            (blockBooking) =>
              blockBooking &&
              blockBooking.startDateTimeLocal &&
              blockBooking.endDateTimeLocal
          )
          .map((blockBooking) => ({
            id: blockBooking.id,
            start: new Date(blockBooking.startDateTimeLocal),
            end: new Date(blockBooking.endDateTimeLocal),
            candidate: blockBooking.candidate || null,
            status: blockBooking.bookingStatus || '',
            isToday:
              this.canCreateBookings &&
              blockBooking.bookingStatus === BookingStatusLabelType.Open &&
              dayjs(blockBooking.startDateTimeLocal).format(
                DateFormatToken.YYYYMMDD
              ) ===
                dayjs()
                  .tz(this.booking.location.timeZone)
                  .format(DateFormatToken.YYYYMMDD),
            canEdit:
              this.canCreateBookings &&
              blockBooking.bookingStatus === BookingStatusLabelType.Open &&
              dayjs(blockBooking.startDateTimeLocal).format(
                DateFormatToken.YYYYMMDD
              ) >
                dayjs()
                  .tz(this.booking.location.timeZone)
                  .format(DateFormatToken.YYYYMMDD),
          }))
          .sort((a, b) => a.start - b.start)
      }
      // single
      return [
        {
          id: this.booking.id,
          start: this.booking.startDateTimeLocal
            ? new Date(this.booking.startDateTimeLocal)
            : null,
          end: this.booking.endDateTimeLocal
            ? new Date(this.booking.endDateTimeLocal)
            : null,
          candidate: this.booking.candidate || null,
          status: this.booking.bookingStatus || '',
          isToday:
            this.canCreateBookings &&
            this.booking.bookingStatus === BookingStatusLabelType.Open &&
            this.isSingleBookingSameDay,
          canEdit:
            this.canCreateBookings &&
            this.booking.bookingStatus === BookingStatusLabelType.Open &&
            dayjs(this.booking.startDateTimeLocal).format(
              DateFormatToken.YYYYMMDD
            ) >
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD),
        },
      ].filter((b) => b.start && b.end)
    },

    daysCount() {
      return this._groupMode ? this._blocks.length : this._blocks.length ? 1 : 0
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    isBlockEdit() {
      if (!this.booking) return false

      const hasBlocks =
        this.booking.blockDetails &&
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      const allBlocksOpenAndFuture =
        hasBlocks &&
        this.booking.blockDetails.every(
          (block) =>
            block.bookingStatus === BookingStatusLabelType.Open &&
            dayjs(block.startDateTimeLocal).format(DateFormatToken.YYYYMMDD) >
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD)
        )

      const bookingCount = hasBlocks ? this.booking.blockDetails.length : 0

      return (
        this.canCreateBookings &&
        this.booking.bookingStatus === BookingStatusLabelType.Open &&
        allBlocksOpenAndFuture &&
        bookingCount === this.booking.openCount
      )
    },
    isDuplicate() {
      if (!this.booking) return false

      const validStatuses = [
        BookingStatusLabelType.Open,
        BookingStatusLabelType.Pending,
        BookingStatusLabelType.Unapproved,
        BookingStatusLabelType.Confirmed,
      ]

      let isValidStatus = false

      const hasBlocks =
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      if (this.canCreateBookings) {
        if (hasBlocks) {
          isValidStatus =
            hasBlocks &&
            this.booking.blockDetails.every((block) =>
              validStatuses.includes(block.bookingStatus)
            )
        } else {
          isValidStatus = validStatuses.includes(this.booking.bookingStatus)
        }
      }

      return this.canCreateBookings && isValidStatus
    },
    isSingleDayEdit() {
      if (!this.booking) return false

      return (
        this.canCreateBookings &&
        this.booking.bookingStatus === BookingStatusLabelType.Open &&
        this.booking.blockDetails === null
      )
    },
    isSingleBookingSameDay() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) ===
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)
      )
    },
    isSingleBookingSameorPastDay() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) <=
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)
      )
    },
    canDuplicateBookings() {
      return this.selfServiceFeatureFlags.enableDuplicateBooking
    },
    canEditBookings() {
      return (
        this.selfServiceFeatureFlags.enableEditBooking &&
        (this.isSingleDayEdit || this.isBlockEdit)
      )
    },
  },

  methods: {
    // display fallback
    _display(v) {
      if (v === null || v === undefined) return '-'
      if (typeof v === 'string' && v.trim() === '') return '-'
      return v
    },
    editBookingBlock() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.Block,
        },
      })
    },
    editBookingSingleDay(date) {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(date).format(DateFormatToken.YYYYMMDD),
        },
      })
    },
    handleEditClick(date) {
      if (this.isSingleDayEdit) {
        if (this.isSingleBookingSameorPastDay) return

        return this.editBookingSingleDay(date)
      }
      return this.editBookingBlock()
    },
    duplicateBooking() {
      const hasBlocks =
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      const bookingMode = hasBlocks ? BookingType.Block : BookingType.SingleDay

      const query = {
        mode: bookingMode,
      }

      if (!hasBlocks) {
        query.date = dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        )
      }

      this.$router.push({
        name: routeDefinitions.bookingsDuplicate.name,
        params: { bookingId: this.booking.id },
        query,
      })
    },
  },
}
</script>

<template>
  <v-navigation-drawer
    v-model="visible"
    app
    fixed
    right
    temporary
    overlay-color="rgba(0,0,0,.35)"
    :width="drawerWidth"
    class="booking-side-panel"
  >
    <div class="close-btn">
      <div class="action-btn">
        <BaseButton
          v-if="canDuplicateBookings"
          icon="mdi-content-copy"
          fab
          tertiary
          circle
          sm
          hover-color="grey lighten-4"
          :class="isDuplicate ? 'panel-copy' : 'panel-copy-disabled'"
          @click="isDuplicate && duplicateBooking()"
        />
        <CustomTooltip
          position="left"
          :text="
            isSingleDayEdit
              ? isSingleBookingSameDay
                ? $t('booking.editTodayBookingWarning')
                : $t('booking.editBooking')
              : $t('booking.editBookingBlock')
          "
        >
          <BaseButton
            v-if="canEditBookings"
            icon="mdi-pencil-outline"
            fab
            tertiary
            circle
            sm
            hover-color="grey lighten-4"
            :class="
              isSingleDayEdit && isSingleBookingSameorPastDay
                ? 'panel-edit-disabled'
                : 'panel-edit'
            "
            :data-button="`booking-edit-${booking.id}`"
            @click="handleEditClick(booking.startDateTimeLocal)"
          />
        </CustomTooltip>
      </div>
      <BaseButton
        icon="mdi-close"
        fab
        tertiary
        circle
        sm
        hover-color="grey lighten-4"
        class="panel-close"
        @click="$emit('input', false)"
      />
    </div>

    <v-divider class="panel-divider"></v-divider>

    <BookingDetailHeader
      :booking="booking"
      :blocks="_blocks"
      :group-mode="_groupMode"
      :display="_display"
    />

    <v-divider class="panel-divider"></v-divider>

    <div class="px-6">
      <v-tabs
        v-model="tab"
        show-arrows
        slider-color="#00ACEF"
        color="#E0E8EE"
        class="panel-tabs"
      >
        <v-tab class="panel-tab">{{ $t('sidePanel.details') }}</v-tab>
        <v-tab class="panel-tab"
          >{{ $t('sidePanel.days') }} ({{ daysCount }})</v-tab
        >
      </v-tabs>
    </div>

    <v-tabs-items v-model="tab" class="px-6">
      <BookingDetailsTab :booking="booking" :display="_display" />

      <BookingDaysTable
        :blocks="_blocks"
        :display="_display"
        :booking-id="booking ? booking.id : 0"
      />
    </v-tabs-items>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/side-panel.scss';
</style>
